import {
  Alert,
  Button,
  CloseButton,
  FieldError,
  Input,
  Label,
  Modal,
  Spinner,
  TextArea,
  TextField,
} from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { OptionsEditor } from "./options-editor";
import { VariantAttributeFormData, variantAttributeSchema } from "./schema";

import {
  useCreateVariantAttribute,
  useUpdateVariantAttribute,
} from "@/hooks/api/variant-attributes";
import { VariantAttribute } from "@/types/product";
import { extractErrorMessage } from "@/utils/error";

interface VariantAttributeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingAttribute?: VariantAttribute | null;
  onSuccess?: () => void;
}

export const VariantAttributeModal = ({
  isOpen,
  onOpenChange,
  editingAttribute = null,
  onSuccess,
}: VariantAttributeModalProps) => {
  const {
    mutate: createAttribute,
    isPending: isCreating,
    isError: isCreateError,
    error: createError,
    reset: resetCreateError,
  } = useCreateVariantAttribute();

  const {
    mutate: updateAttribute,
    isPending: isUpdating,
    isError: isUpdateError,
    error: updateError,
    reset: resetUpdateError,
  } = useUpdateVariantAttribute();

  const form = useForm<VariantAttributeFormData>({
    resolver: zodResolver(variantAttributeSchema),
    mode: "all",
    defaultValues: {
      name: "",
      description: "",
      options: [{ name: "", value: "" }],
    },
  });

  const isLoading = isCreating || isUpdating;
  const isError = isCreateError || isUpdateError;
  const error = createError || updateError;

  useEffect(() => {
    if (isOpen) {
      if (editingAttribute) {
        form.reset({
          name: editingAttribute.name,
          description: editingAttribute.description || "",
          options:
            editingAttribute.options?.map((o) => ({
              optionId: o.id,
              name: o.name,
              value: o.value,
            })) || [],
        });
      } else {
        form.reset({
          name: "",
          description: "",
          options: [{ name: "", value: "" }],
        });
      }
      resetCreateError();
      resetUpdateError();
    }
  }, [isOpen, editingAttribute, form, resetCreateError, resetUpdateError]);

  const handleSubmit = (values: VariantAttributeFormData) => {
    const mappedOptions = values.options.map(({ optionId, ...rest }) => ({
      ...rest,
      ...(optionId && { id: optionId }),
    }));

    if (editingAttribute) {
      updateAttribute(
        {
          id: editingAttribute.id,
          payload: {
            name: values.name,
            description: values.description,
            options: mappedOptions,
          },
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            onSuccess?.();
          },
        },
      );
    } else {
      createAttribute(
        {
          name: values.name,
          description: values.description,
          options: mappedOptions,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            onSuccess?.();
          },
        },
      );
    }
  };

  const resetError = () => {
    resetCreateError();
    resetUpdateError();
  };

  return (
    <Modal>
      <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
        <Modal.Container size="lg">
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <FormProvider {...form}>
              <Modal.Header>
                <Modal.Heading>
                  {editingAttribute ? "Edit Attribute" : "Create Attribute"}
                </Modal.Heading>
              </Modal.Header>
              <Modal.Body className="p-1">
                <form
                  className="flex flex-col gap-4"
                  id="attribute-form"
                  onSubmit={form.handleSubmit(handleSubmit)}
                >
                  {isError && (
                    <Alert status="danger" className="bg-surface-secondary">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>Error</Alert.Title>
                        <Alert.Description>
                          {extractErrorMessage(error)}
                        </Alert.Description>
                      </Alert.Content>
                      <CloseButton aria-label="Close" onPress={resetError} />
                    </Alert>
                  )}

                  <Controller
                    control={form.control}
                    name="name"
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <TextField
                        isInvalid={Boolean(fieldError?.message)}
                        name={field.name}
                        value={field.value ?? ""}
                        variant="secondary"
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      >
                        <Label>Name</Label>
                        <Input
                          ref={field.ref}
                          autoFocus
                          placeholder="e.g., Size, Color, Weight"
                        />
                        {fieldError?.message ? (
                          <FieldError>{fieldError.message}</FieldError>
                        ) : null}
                      </TextField>
                    )}
                  />

                  <Controller
                    control={form.control}
                    name="description"
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <TextField
                        isInvalid={Boolean(fieldError?.message)}
                        name={field.name}
                        value={field.value ?? ""}
                        variant="secondary"
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      >
                        <Label>Description</Label>
                        <TextArea
                          ref={field.ref}
                          placeholder="Optional description for this attribute"
                          rows={3}
                        />
                        {fieldError?.message ? (
                          <FieldError>{fieldError.message}</FieldError>
                        ) : null}
                      </TextField>
                    )}
                  />

                  <OptionsEditor />
                </form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  isDisabled={isLoading}
                  variant="tertiary"
                  onPress={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  form="attribute-form"
                  isDisabled={isLoading}
                  isPending={isLoading}
                  type="submit"
                  variant="primary"
                >
                  {({ isPending }) => (
                    <>
                      {isPending && <Spinner color="current" size="sm" />}
                      {editingAttribute ? "Update" : "Create"}
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </FormProvider>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
};
